import React, { useEffect, useMemo, useState } from "react";
import DataGrid, {
  Column,
  Row,
  RowRendererProps,
  SortColumn,
} from "react-data-grid";
import "./index.scss";

export type TableArgs<T extends { id: number | string }> = {
  columns: Column<T>[];
  data: T[];
  onRowClick?: (values: T) => void;
  activeItemId?: number | string;
  defaultSorted?: SortColumn;
};

function Table<T extends { id: number | string }>({
  columns,
  data,
  onRowClick,
  activeItemId,
  defaultSorted,
}: TableArgs<T>) {
  const [sortColumns, setSortColumns] = useState<readonly SortColumn[]>([]);
  useEffect(() => {
    if (defaultSorted) {
      setSortColumns([defaultSorted as SortColumn]);
    }
  }, [defaultSorted]);

  function MyRowRenderer(props: RowRendererProps<T>) {
    return (
      <Row
        {...props}
        className={
          props.className +
          `${activeItemId === props.row.id ? " active" : ""}` +
          `${onRowClick ? " is-clickable" : ""}`
        }
      />
    );
  }

  const sortedRows = useMemo((): readonly T[] => {
    if (sortColumns.length === 0) return data;

    const sortedRows = [...data];
    sortedRows.sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = (a: T, b: T) => {
          const type = typeof a[sort.columnKey as keyof T];
          const f = a[sort.columnKey as keyof T];
          const s = b[sort.columnKey as keyof T];
          switch (type) {
            case "string":
              return `${f}`.localeCompare(`${s}`);
            default:
              return parseInt(`${f}`) - parseInt(`${s}`);
          }
        };
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === "ASC" ? compResult : -compResult;
        }
      }
      return 0;
    });
    return sortedRows;
  }, [data, sortColumns]);

  return (
    <DataGrid
      sortColumns={sortColumns}
      onSortColumnsChange={setSortColumns}
      enableVirtualization={false}
      columns={columns}
      rows={sortedRows}
      onRowClick={onRowClick}
      rowRenderer={MyRowRenderer}
      defaultColumnOptions={{ sortable: true }}
      rowHeight={40}
      headerRowHeight={38}
    />
  );
}

export default Table;
